'use client';

import Link from 'next/link';
import type { MouseEvent, ReactNode } from 'react';
import { markOpenedInPlace } from '@/app/lib/inPlaceNavigation';
import { readableCourseTitle } from '@/app/lib/courseTitle';
import { useCoursePeek } from './CoursePeekProvider';
import type { CoursePageContext } from './types';

/**
 * The one way to make a course name open its details. Inside the app it opens
 * the shared course view with this page's context; without the provider (or on
 * a modified click) it is an ordinary link to the course in Explore.
 */
export function CourseLink({
  courseId,
  semester,
  context,
  children,
  className = '',
  onOpen,
}: {
  courseId: string;
  semester?: string;
  context?: CoursePageContext;
  children: ReactNode;
  className?: string;
  /** Lets a route react (e.g. close its own menu) before the view opens. */
  onOpen?: () => void;
}) {
  const peek = useCoursePeek();
  const params = new URLSearchParams({ course: courseId, ...(semester ? { semester } : {}) });
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!peek || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    markOpenedInPlace(event);
    onOpen?.();
    peek.openCourse({ courseId, semester, context });
  };
  return (
    <Link
      href={`/explore?${params}`}
      onClick={handleClick}
      className={`rounded-sm underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${className}`}
      data-course-link={courseId}
    >
      {/* Catalog names arrive in capitals; plain-text names are shown in readable title case. */}
      {typeof children === 'string' ? readableCourseTitle(children) : children}
    </Link>
  );
}

/**
 * For existing links to a full course page (`/courses/01198111`): a plain click
 * opens the shared course view in place, and modified clicks keep the page.
 */
export function useCoursePageLinkClick(context?: CoursePageContext, semester?: string) {
  const peek = useCoursePeek();
  return (event: MouseEvent<HTMLElement>, href: string | undefined) => {
    const courseId = href?.match(/^\/courses\/(\d{8})(?:[?#].*)?$/)?.[1];
    if (!peek || !courseId || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return false;
    event.preventDefault();
    markOpenedInPlace(event);
    peek.openCourse({ courseId, semester, context });
    return true;
  };
}
